/**
 * DeliveryDetailTab Component
 * 배달 상세 탭 (기존 배달이력 페이지)
 */
import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatsCard } from './shared/StatsCard';
import { FeeCard } from './shared/FeeCard';
import { SlotsCard } from './shared/SlotsCard';
import { useDeliveryHistory } from '../hooks/useDeliveryHistory';
import { useDeferredLoading } from '../../../../core/hooks/useDeferredLoading';
import { getUserInfo } from '../../../../core/utils';
import { getRiderSlotData } from '../../../home/api/RiderHomeApi';
import styles from '../styles/DeliveryDetailTab.module.css';

type TabType = 'summary' | 'detail';

interface DeliveryDetailTabProps {
    activeTab: TabType;
    onTabChange: (tab: TabType) => void;
}

export const DeliveryDetailTab: React.FC<DeliveryDetailTabProps> = ({ activeTab, onTabChange }) => {
    const navigate = useNavigate();
    const fromDateRef = useRef<HTMLInputElement>(null);
    const toDateRef = useRef<HTMLInputElement>(null);

    const isSupplier = useMemo(() => {
        const userInfo = getUserInfo();
        return !!userInfo?.suppId;
    }, []);

    const {
        deliveryStats,
        fromDate,
        toDate,
        preAcctYn,
        handleFromDateChange,
        handleToDateChange,
        isLoading: rawIsLoading,
    } = useDeliveryHistory();

    const [slotNames, setSlotNames] = useState<string[]>([]);
    const [slotTimeRanges, setSlotTimeRanges] = useState<string[]>([]);
    const [slotCounts, setSlotCounts] = useState<number[]>([]);
    const [isSlotLoading, setIsSlotLoading] = useState(true);

    const calcSlotCountsFromHours = (hourCounts: number[], ranges: string[]): number[] =>
        ranges.map(range => {
            const match = range.match(/(\d{2}):(\d{2})\s*~\s*(\d{2}):(\d{2})/);
            if (!match) return 0;
            const start = parseInt(match[1], 10);
            const end = parseInt(match[3], 10);
            if (start <= end) {
                let total = 0;
                for (let h = start; h <= end; h++) total += hourCounts[h] ?? 0;
                return total;
            }
            let total = 0;
            for (let h = start; h <= 23; h++) total += hourCounts[h] ?? 0;
            for (let h = 0; h <= end; h++) total += hourCounts[h] ?? 0;
            return total;
        });

    useEffect(() => {
        if (!fromDate || !toDate) return;
        const fromDateYYYYMMDD = fromDate.replace(/-/g, '');
        const toDateYYYYMMDD = toDate.replace(/-/g, '');
        setIsSlotLoading(true);
        getRiderSlotData(fromDateYYYYMMDD, toDateYYYYMMDD).then(data => {
            setSlotNames(data.slotNames);
            setSlotTimeRanges(data.slotTimeRanges);
            setIsSlotLoading(false);
        });
    }, [fromDate, toDate]);

    useEffect(() => {
        if (slotTimeRanges.length === 0) return;
        setSlotCounts(calcSlotCountsFromHours(deliveryStats.hourCounts, slotTimeRanges));
    }, [deliveryStats.hourCounts, slotTimeRanges]);

    const isLoading = useDeferredLoading(rawIsLoading || isSlotLoading, 200);

    const formatDateToYyyymmdd = (dateString: string): string => {
        if (!dateString) return '';
        return dateString.replace(/-/g, '');
    };

    const handleSlotClick = (slotType: string, slotName: string) => {
        if (isSupplier || !fromDate || !toDate) {
            return;
        }

        const params = new URLSearchParams();
        params.set('fromDate', formatDateToYyyymmdd(fromDate));
        params.set('toDate', formatDateToYyyymmdd(toDate));
        params.set('slotType', slotType);
        params.set('slotName', slotName);

        navigate(`/delivery-history/detail?${params.toString()}`);
    };

    const isWeeklySettlement = () => {
        return preAcctYn === 'N';
    };

    const handleFromDateIconClick = () => {
        if (fromDateRef.current) {
            fromDateRef.current.focus();
            fromDateRef.current.showPicker?.();
        }
    };

    const handleToDateIconClick = () => {
        if (toDateRef.current) {
            toDateRef.current.focus();
            toDateRef.current.showPicker?.();
        }
    };

    const handleFromDateChangeWithRestriction = (value: string) => {
        if (isWeeklySettlement()) {
            const selectedDate = new Date(value);
            const dayOfWeek = selectedDate.getDay();
            if (dayOfWeek !== 3) {
                return;
            }
        }
        handleFromDateChange(value);
    };

    const getWednesdayOnlyDateRange = () => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();

        const sixMonthsAgo = new Date(currentYear, currentMonth - 6, 1);
        const firstWednesday = new Date(sixMonthsAgo);
        const dayOfWeek = sixMonthsAgo.getDay();
        const daysToAdd = dayOfWeek <= 3 ? (3 - dayOfWeek) : (10 - dayOfWeek);
        firstWednesday.setDate(sixMonthsAgo.getDate() + daysToAdd);

        const sixMonthsLater = new Date(currentYear, currentMonth + 6, 1);
        const lastWednesday = new Date(sixMonthsLater);
        const lastDayOfWeek = sixMonthsLater.getDay();
        const daysToSubtract = lastDayOfWeek >= 3 ? (lastDayOfWeek - 3) : (lastDayOfWeek + 4);
        lastWednesday.setDate(sixMonthsLater.getDate() - daysToSubtract);

        return {
            min: firstWednesday.toISOString().split('T')[0],
            max: lastWednesday.toISOString().split('T')[0]
        };
    };

    return (
        <>
            {/* 통합 필터 카드 */}
            <div className={styles['filter-section']}>
                {/* 탭 */}
                <div className={styles['tabs']}>
                    <button
                        className={`${styles['tab']} ${activeTab === 'summary' ? styles['tab-active'] : ''}`}
                        onClick={() => onTabChange('summary')}
                    >
                        요약
                    </button>
                    <button
                        className={`${styles['tab']} ${activeTab === 'detail' ? styles['tab-active'] : ''}`}
                        onClick={() => onTabChange('detail')}
                    >
                        상세
                    </button>
                </div>
                {/* 구분선 */}
                <div className={styles['filter-divider']} />
                {/* 날짜 범위 */}
                <div className={styles['date-range-wrapper']}>
                    <div className={styles['date-input-box']}>
                        <input
                            ref={fromDateRef}
                            type="date"
                            className={styles['date-input']}
                            value={fromDate}
                            onChange={(e) => isWeeklySettlement() ? handleFromDateChangeWithRestriction(e.target.value) : handleFromDateChange(e.target.value)}
                            onKeyDown={(e) => e.preventDefault()}
                            min={isWeeklySettlement() ? getWednesdayOnlyDateRange().min : undefined}
                            max={isWeeklySettlement() ? getWednesdayOnlyDateRange().max : undefined}
                            title={isWeeklySettlement() ? "주정산 기간에는 수요일만 선택 가능합니다" : undefined}
                        />
                        <i className={`fas fa-calendar-alt ${styles['date-icon']}`} onClick={handleFromDateIconClick}></i>
                    </div>
                    <span className={styles['date-separator']}>~</span>
                    <div className={styles['date-input-box']}>
                        <input
                            ref={toDateRef}
                            type="date"
                            className={`${styles['date-input']} ${isWeeklySettlement() ? styles['disabled'] : ''}`}
                            value={toDate}
                            onChange={(e) => handleToDateChange(e.target.value)}
                            onKeyDown={(e) => e.preventDefault()}
                            disabled={isWeeklySettlement()}
                        />
                        <i
                            className={`fas fa-calendar-alt ${styles['date-icon']} ${isWeeklySettlement() ? styles['disabled'] : ''}`}
                            onClick={isWeeklySettlement() ? undefined : handleToDateIconClick}
                        ></i>
                    </div>
                </div>
            </div>

            {/* 메인 컨텐츠 */}
            <>
                {isLoading ? (
                    <div className={styles['loading-section']}>
                        <div className={styles['loading-spinner']}></div>
                        <p className={styles['loading-text']}>배달 이력을 불러오는 중...</p>
                    </div>
                ) : (
                    <>
                        <FeeCard deliveryFee={deliveryStats.deliveryFee} />

                        <StatsCard
                            completedCount={deliveryStats.completedCount}
                            rejectedCount={deliveryStats.rejectedCount}
                            acceptanceRate={deliveryStats.acceptanceRate}
                            foodCount={deliveryStats.foodCount}
                            bmartCount={deliveryStats.bmartCount}
                            storeCount={deliveryStats.storeCount}
                        />

                        <SlotsCard
                            morningLunchPeakCount={deliveryStats.morningLunchPeakCount}
                            afternoonNonPeakCount={deliveryStats.afternoonNonPeakCount}
                            eveningPeakCount={deliveryStats.eveningPeakCount}
                            nightPeakCount={deliveryStats.nightPeakCount}
                            slotNames={slotNames}
                            slotCounts={slotCounts}
                            slotTimeRanges={slotTimeRanges}
                            onSlotClick={handleSlotClick}
                            clickable={!isSupplier}
                        />
                    </>
                )}
            </>
        </>
    );
};
